const META_KEY = 'hms.lab.catalog.pilot.meta'

export type CatalogPilotMeta = {
  /** investigationMasterId → alias strings */
  aliases: Record<string, string[]>
  /** Explicit draft markers (in addition to inactive enterprise rows) */
  draftMasterIds: string[]
}

function emptyMeta(): CatalogPilotMeta {
  return { aliases: {}, draftMasterIds: [] }
}

export function loadCatalogPilotMeta(): CatalogPilotMeta {
  try {
    const raw = sessionStorage.getItem(META_KEY)
    if (!raw) return emptyMeta()
    const parsed = JSON.parse(raw) as CatalogPilotMeta
    return {
      aliases: parsed.aliases ?? {},
      draftMasterIds: Array.isArray(parsed.draftMasterIds) ? parsed.draftMasterIds : [],
    }
  } catch {
    return emptyMeta()
  }
}

export function saveCatalogPilotMeta(meta: CatalogPilotMeta): void {
  sessionStorage.setItem(META_KEY, JSON.stringify(meta))
}

export function getAliasesForMaster(masterId: string): string[] {
  return loadCatalogPilotMeta().aliases[masterId] ?? []
}

export function setAliasesForMaster(masterId: string, aliases: string[]): void {
  const meta = loadCatalogPilotMeta()
  const next = aliases.map((a) => a.trim()).filter(Boolean)
  if (next.length === 0) {
    const { [masterId]: _, ...rest } = meta.aliases
    meta.aliases = rest
  } else {
    meta.aliases = { ...meta.aliases, [masterId]: next }
  }
  saveCatalogPilotMeta(meta)
}

export function markDraftMaster(masterId: string): void {
  const meta = loadCatalogPilotMeta()
  if (!meta.draftMasterIds.includes(masterId)) {
    meta.draftMasterIds = [...meta.draftMasterIds, masterId]
    saveCatalogPilotMeta(meta)
  }
}

export function clearDraftMaster(masterId: string): void {
  const meta = loadCatalogPilotMeta()
  meta.draftMasterIds = meta.draftMasterIds.filter((id) => id !== masterId)
  saveCatalogPilotMeta(meta)
}

export function isLocalInvestigationCode(code: string): boolean {
  return code.trim().toUpperCase().startsWith('LOCAL-')
}

export function buildLocalInvestigationCode(hospitalCode: string, displayName: string): string {
  const hosp = (hospitalCode || 'HOSP').replace(/[^a-zA-Z0-9]/g, '').slice(0, 12).toUpperCase() || 'HOSP'
  const slug =
    displayName
      .trim()
      .slice(0, 40)
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toUpperCase() || 'TEST'
  return `LOCAL-${hosp}-${slug}`
}
